<template>
    <LoadingComponent :props="loading" />

    <!-- SECCIÓN 1: CONFIGURACIÓN BASE (FLUJO GENERAL INDEPENDIENTE) -->
    <div class="db-card mb-6" :class="{ 'on-update': onUpdateActive }">
        <div class="db-card-header items-center justify-between gap-4">
            <div>
                <h3 class="db-card-title pt-1">{{ $t('menu.ai_import_settings') }} - {{ $t('label.base_config_title') }}</h3>
                <p class="text-xs text-gray-500 mt-0.5">{{ $t('label.base_config_desc') }}</p>
            </div>
            <div class="flex items-center gap-2">
                <span class="text-sm">{{ $t('label.ai_import_enabled') }}</span>
                <div class="custom-switch">
                    <input
                        type="checkbox"
                        :checked="!!importForm.is_enabled"
                        @change="importForm.is_enabled = $event.target.checked"
                    >
                </div>
                <button
                    type="button"
                    class="db-btn h-9 py-2 px-3 text-white bg-primary ml-3"
                    @click="refreshFromServer"
                    :disabled="isRefreshingView"
                    :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                >
                    <template v-if="isRefreshingView">
                        <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </template>
                </button>
            </div>
        </div>
        <div class="db-card-body">
            <form @submit.prevent="saveImportSettings" class="w-full d-block">
                <div class="form-row">

                    <div class="form-col-12 border-b pb-3 mb-3" v-if="!importForm.is_enabled">
                        <p class="db-field-alert text-amber-600 text-sm">
                            {{ $t('message.ai_import_disabled') }}
                        </p>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.max_items_per_document') }}</label>
                        <vue-select
                            :key="documentSizeSelectKey"
                            class="db-field-control f-b-custom-select"
                            :class="errors.max_items_per_document ? 'invalid' : ''"
                            v-model="importForm.max_items_per_document"
                            :options="documentSizeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                        <small class="db-field-alert" v-if="errors.max_items_per_document">{{ errors.max_items_per_document[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.max_items_per_chunk') }}</label>
                        <vue-select
                            :key="chunkSizeSelectKey"
                            class="db-field-control f-b-custom-select"
                            :class="errors.max_items_per_chunk ? 'invalid' : ''"
                            v-model="importForm.max_items_per_chunk"
                            :options="chunkSizeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                        <small class="db-field-alert" v-if="errors.max_items_per_chunk">{{ errors.max_items_per_chunk[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_provider') }}</label>
                        <vue-select
                            :key="providerSelectKey"
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_provider"
                            :options="safeProviderOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                        <small class="db-field-alert" v-if="errors.default_provider">{{ errors.default_provider[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_model') }}</label>
                        <vue-select
                            :key="modelSelectKey"
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_model"
                            :options="safeActiveModelOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                        <small class="db-field-alert" v-if="errors.default_model">{{ errors.default_model[0] }}</small>
                    </div>

                    <div class="form-col-12">
                        <label class="db-field-title">{{ $t('label.default_user_prompt') }}</label>
                        <textarea v-model="importForm.default_user_prompt" class="db-field-control" rows="3" :class="errors.default_user_prompt ? 'invalid' : ''"></textarea>
                        <small class="db-field-alert" v-if="errors.default_user_prompt">{{ errors.default_user_prompt[0] }}</small>
                    </div>

                    <div class="form-col-12">
                        <label class="db-field-title">{{ $t('label.default_custom_fields_json') }}</label>
                        <textarea v-model="customFieldsJson" class="db-field-control" rows="5" :class="errors.default_custom_fields ? 'invalid' : ''"></textarea>
                        <small class="db-field-alert" v-if="errors.default_custom_fields">{{ errors.default_custom_fields[0] }}</small>
                    </div>

                    <hr class="w-full border-gray-100 my-4" />

                    <!-- BÚSQUEDAS WEB CON LLM -->
                    <div class="form-col-12 mb-2">
                        <h4 class="font-client font-medium text-base text-gray-800 flex items-center gap-2">
                            <i class="fa-solid fa-globe text-primary"></i>
                            <span>{{ $t('label.web_search_title') }}</span>
                        </h4>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.web_search_scope') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.web_search_scope"
                            :options="webSearchScopeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                        <small class="text-xs">{{ $t('label.web_search_scope_hint') }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.web_search_order') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.web_search_order"
                            :options="webSearchOrderOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                        <small class="text-xs">{{ $t('label.web_search_order_hint') }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.web_enrichment') }}</label>
                        <div class="flex items-center gap-3 border h-10 rounded-md">
                            <div class="flex items-center justify-center py-2 px-3 border-r">
                                <div class="custom-switch">
                                    <input
                                        type="checkbox"
                                        :checked="!!importForm.extra.web_enrichment_enabled"
                                        @change="importForm.extra.web_enrichment_enabled = $event.target.checked"
                                    >
                                </div>
                            </div>
                            <small class="text-xs">{{ $t('message.web_enrichment_hint') }}</small>
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-6" v-if="importForm.extra.web_enrichment_enabled">
                        <label class="db-field-title">{{ $t('label.web_enrichment_overwrite') }}</label>
                        <div class="flex items-center gap-3 border h-10 rounded-md">
                            <div class="flex items-center justify-center py-2 px-3 border-r">
                                <div class="custom-switch">
                                    <input
                                        type="checkbox"
                                        :checked="!!importForm.extra.web_enrichment_overwrite"
                                        @change="importForm.extra.web_enrichment_overwrite = $event.target.checked"
                                    >
                                </div>
                            </div>
                            <small class="text-xs">{{ $t('message.web_enrichment_overwrite_hint') }}</small>
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-6" v-if="importForm.extra.web_enrichment_enabled">
                        <label class="db-field-title">{{ $t('label.web_enrichment_image_enabled') }}</label>
                        <div class="flex items-center gap-3 border h-10 rounded-md">
                            <div class="flex items-center justify-center py-2 px-3 border-r">
                                <div class="custom-switch">
                                    <input
                                        type="checkbox"
                                        :checked="!!importForm.extra.web_enrichment_image_enabled"
                                        @change="importForm.extra.web_enrichment_image_enabled = $event.target.checked"
                                    >
                                </div>
                            </div>
                            <small class="text-xs">{{ $t('message.web_enrichment_image_hint') }}</small>
                        </div>
                    </div>

                    <hr class="w-full border-gray-100 my-4" />

                    <!-- PARÁMETROS PREDETERMINADOS DE E-COMMERCE -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.default_tax_ids') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_tax_ids"
                            :options="taxOptions"
                            label-by="name"
                            value-by="id"
                            :multiple="true"
                            :closeOnSelect="false"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.default_unit_id') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_unit_id"
                            :options="unitOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_wholesale') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_wholesale"
                            :options="askOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.default_wholesale_min') }}</label>
                        <input v-model.number="importForm.default_wholesale_min" type="number" min="1" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_only_stock') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_only_stock"
                            :options="activityOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_show_soldout') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_show_soldout"
                            :options="activityOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_refundable') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_refundable"
                            :options="askOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_max_purchase_qty') }}</label>
                        <input v-model.number="importForm.default_max_purchase_qty" type="number" min="1" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.default_low_stock_alert_at') }}</label>
                        <input v-model.number="importForm.default_low_stock_alert_at" type="number" min="1" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.barcode') }}</label>
                        <vue-select
                            :key="barcodeSelectKey"
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.default_barcode_id"
                            :options="safeBarcodeTypeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.default_warranty') }}</label>
                        <input v-model="importForm.default_warranty" type="text" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.default_language') }}</label>
                        <vue-select
                            :key="languageSelectKey"
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.language"
                            :options="safeLanguageOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.system_prompt_mode') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.system_prompt_mode"
                            :options="systemPromptModeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                    </div>

                    <hr class="w-full border-gray-100 my-4" />

                    <!-- PIPELINE DE TRATAMIENTO DE IMÁGENES -->
                    <div class="form-col-12 mb-2">
                        <h4 class="font-client font-medium text-base text-gray-800 flex items-center gap-2">
                            <i class="fa-solid fa-image text-primary"></i>
                            <span>{{ $t('label.image_processing_title') }}</span>
                        </h4>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.include_cover_image') }}</label>
                        <div class="flex items-center gap-3 border h-10 rounded-md">
                            <div class="flex items-center justify-center py-2 px-3 border-r">
                                <div class="custom-switch">
                                    <input
                                        type="checkbox"
                                        :checked="!!importForm.extra.image_pipeline.include_cover_image"
                                        @change="importForm.extra.image_pipeline.include_cover_image = $event.target.checked"
                                    >
                                </div>
                            </div>
                            <small class="text-xs">{{ $t('message.include_cover_image_hint') }}</small>
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.image_max_dimension') }}</label>
                        <input v-model.number="importForm.extra.image_pipeline.max_dimension" type="number" min="320" max="4096" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.image_min_dimension') }}</label>
                        <input v-model.number="importForm.extra.image_pipeline.min_dimension" type="number" min="100" max="4096" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.image_optimization_percentage') }}</label>
                        <input v-model.number="importForm.extra.image_pipeline.optimization_percentage" type="number" min="0" max="90" class="db-field-control" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.image_treatment_type') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.image_pipeline.treatment_type"
                            :options="treatmentTypeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6" v-if="Number(importForm.extra.image_pipeline.treatment_type) === 2">
                        <label class="db-field-title required">{{ $t('label.image_crop_x_align') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.image_pipeline.crop_x_align"
                            :options="cropXAlignOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6" v-if="Number(importForm.extra.image_pipeline.treatment_type) === 2">
                        <label class="db-field-title required">{{ $t('label.image_crop_y_align') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="importForm.extra.image_pipeline.crop_y_align"
                            :options="cropYAlignOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                    </div>

                    <div class="form-col-12 sm:form-col-6" v-if="Number(importForm.extra.image_pipeline.treatment_type) === 3">
                        <label class="db-field-title required">{{ $t('label.image_padding_color') }}</label>
                        <div class="flex gap-1">
                            <input 
                                v-model="importForm.extra.image_pipeline.padding_color" 
                                type="color" 
                                class="input-color h-10 w-14 border rounded cursor-pointer"
                            />
                            <input 
                                v-model="importForm.extra.image_pipeline.padding_color" 
                                type="text" 
                                placeholder="#FFFFFF" 
                                class="db-field-control w-full"
                            />
                        </div>
                    </div>

                    <!-- BOTÓN DE GUARDADO INDEPENDIENTE PARA CONFIGURACIÓN BASE -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <button type="submit" class="db-btn text-white bg-primary flex items-center gap-2" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>{{ $t('label.save_base_config') }}</span>
                            </template>
                        </button>
                    </div>

                </div>
            </form>
        </div>
    </div>

    <!-- SECCIÓN 2: INTEGRACIÓN DE PLUGINS POR TIPO DE PRODUCTO -->
    <div class="db-card mb-6">
        <div class="db-card-header flex items-center justify-between">
            <div>
                <h3 class="db-card-title pt-1 flex items-center gap-2">
                    <i class="fa-solid fa-puzzle-piece text-primary"></i>
                    <span>{{ $t('label.integrated_modules_title') }}</span>
                </h3>
                <p class="text-xs text-gray-500 mt-0.5">{{ $t('label.integrated_modules_desc') }}</p>
            </div>
        </div>
        <div class="db-card-body">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <!-- CARD PLUGIN 1: JUEGOS DE MESA (BOARDGAMEGEEK) -->
                <div
                    class="p-4 rounded-xl flex items-start justify-between gap-4 transition"
                    :class="importForm.extra.bgg_api_enabled ? 'bg-blue-50 border border-blue-500' : 'bg-white border border-gray-200 opacity-90'"
                >
                    <div class="flex items-center gap-4">
                        <div
                            class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition"
                            :class="importForm.extra.bgg_api_enabled ? 'bg-blue-100 text-blue-500' : 'bg-gray-100 text-gray-500'"
                        >
                            <i class="fa-solid fa-dice-d20 text-2xl"></i>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <h4 class="font-semibold text-sm" :class="importForm.extra.bgg_api_enabled ? 'text-blue-500' : 'text-gray-500'">{{ $t('label.board_games_title') }}</h4>
                                <span class="px-1 py-0 text-xs font-semibold rounded border" :class="importForm.extra.bgg_api_enabled ? 'border-blue-500 text-blue-500' : 'bg-gray-100 text-gray-500'">
                                    {{ importForm.extra.bgg_api_enabled ? $t('label.active') : $t('label.available') }}
                                </span>
                            </div>
                            <p class="text-xs mt-1 leading-relaxed"
                                :class="importForm.extra.bgg_api_enabled ? 'text-blue-500' : 'text-gray-500'">
                                {{ $t('label.board_games_desc') }}
                            </p>
                        </div>
                    </div>
                    <div class="custom-switch flex-shrink-0 mt-1">
                        <input
                            type="checkbox"
                            :checked="!!importForm.extra.bgg_api_enabled"
                            @change="togglePlugin('board_games', $event.target.checked)"
                        >
                    </div>
                </div>

                <!-- CARD PLUGIN 2: MASCOTAS (OPACIDAD 50%) -->
                <div class="p-4 border border-gray-200 rounded-xl bg-white opacity-50 flex items-start justify-between gap-4">
                    <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
                            <i class="fa-solid fa-paw text-2xl"></i>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <h4 class="font-semibold text-gray-500 text-sm">{{ $t('label.pets_plugin_title') }}</h4>
                                <span class="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-500">{{ $t('label.coming_soon') }}</span>
                            </div>
                            <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                                {{ $t('label.pets_plugin_desc') }}
                            </p>
                        </div>
                    </div>
                    <div class="custom-switch flex-shrink-0 mt-1 opacity-50 pointer-events-none">
                        <input type="checkbox" :checked="false" disabled>
                    </div>
                </div>

                <!-- CARD PLUGIN 3: MUEBLES Y HOGAR (OPACIDAD 50%) -->
                <div class="p-4 border border-gray-200 rounded-xl bg-white opacity-50 flex items-start justify-between gap-4">
                    <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
                            <i class="fa-solid fa-couch text-2xl"></i>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <h4 class="font-semibold text-gray-500 text-sm">{{ $t('label.furniture_plugin_title') }}</h4>
                                <span class="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-500">{{ $t('label.coming_soon') }}</span>
                            </div>
                            <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                                {{ $t('label.furniture_plugin_desc') }}
                            </p>
                        </div>
                    </div>
                    <div class="custom-switch flex-shrink-0 mt-1 opacity-50 pointer-events-none">
                        <input type="checkbox" :checked="false" disabled>
                    </div>
                </div>

                <!-- CARD PLUGIN 4: REFACCIONES AUTOMOTRICES (OPACIDAD 50%) -->
                <div class="p-4 border border-gray-200 rounded-xl bg-white opacity-50 flex items-start justify-between gap-4">
                    <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
                            <i class="fa-solid fa-gears text-2xl"></i>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <h4 class="font-semibold text-gray-500 text-sm">{{ $t('label.auto_parts_plugin_title') }}</h4>
                                <span class="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-500">{{ $t('label.coming_soon') }}</span>
                            </div>
                            <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                                {{ $t('label.auto_parts_plugin_desc') }}
                            </p>
                        </div>
                    </div>
                    <div class="custom-switch flex-shrink-0 mt-1 opacity-50 pointer-events-none">
                        <input type="checkbox" :checked="false" disabled>
                    </div>
                </div>

            </div>
        </div>
    </div>

    <!-- SECCIÓN 3: ÁREA DE CONFIGURACIÓN DE MÓDULOS ACTIVOS (TABS DE MÓDULOS) -->
    <div class="db-card">
        <div class="db-card-header flex items-center justify-between p-2 pl-4">
            <h3 class="db-card-title pt-1 flex items-center gap-2">
                <i class="fa-solid fa-sliders text-primary"></i>
                <span>{{ $t('label.active_module_params_title') }}</span>
            </h3>
            <div class="w-fit">
                <div class="inline-flex rounded-lg bg-gray-100 p-1 gap-1">
                    <button
                        v-if="importForm.extra.bgg_api_enabled"
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition flex items-center gap-2"
                        :class="activeModuleTab === 'board_games' ? 'bg-white shadow-sm text-primary font-semibold' : 'text-gray-600 hover:text-primary'"
                        @click.prevent="setActiveModuleTab('board_games')"
                    >
                        <i class="fa-solid fa-dice-d20"></i>
                        <span>{{ $t('label.board_games_tab') }}</span>
                    </button>
                    <button
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition flex items-center gap-2"
                        :class="activeModuleTab === 'web_sources' ? 'bg-white shadow-sm text-primary font-semibold' : 'text-gray-600 hover:text-primary'"
                        @click.prevent="setActiveModuleTab('web_sources')"
                    >
                        <i class="fa-solid fa-globe"></i>
                        <span>{{ $t('label.web_sources_tab') }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div class="db-card-body">
            <!-- TAB 1: CONFIGURACIÓN DE BOARDGAMEGEEK (BGG API) -->
            <form v-if="activeModuleTab === 'board_games' && importForm.extra.bgg_api_enabled" @submit.prevent="saveImportSettings" class="w-full d-block">
                <div class="form-row">
                    <div class="form-col-12 border-b pb-3 mb-3">
                        <h4 class="font-client font-medium text-base flex items-center gap-2">
                            <span>{{ $t('label.bgg_api_config') }}</span>
                            <span class="px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-500" v-if="importForm.extra.bgg_api_enabled">{{ $t('label.active_module_badge') }}</span>
                            <span class="px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500" v-else>{{ $t('label.inactive_module_badge') }}</span>
                        </h4>
                        <p class="text-xs text-gray-500 mt-1">{{ $t('label.bgg_api_desc') }}</p>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.bgg_api_base_url') }}</label>
                        <input v-model="importForm.extra.bgg_api_base_url" type="text" class="db-field-control" placeholder="https://api.boardgamegeek.com/xmlapi2" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.bgg_api_search_endpoint') }}</label>
                        <input v-model="importForm.extra.bgg_api_search_endpoint" type="text" class="db-field-control" placeholder="/search" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.bgg_api_detail_endpoint') }}</label>
                        <input v-model="importForm.extra.bgg_api_detail_endpoint" type="text" class="db-field-control" placeholder="/thing" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.bgg_api_token') }}</label>
                        <input v-model="importForm.extra.bgg_api_token" type="text" class="db-field-control" :placeholder="$t('label.bgg_token_placeholder')" />
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.bgg_api_safety_interval') }}</label>
                        <input v-model.number="importForm.extra.bgg_api_safety_interval" type="number" min="0" max="60" class="db-field-control" placeholder="5" />
                        <small class="text-xs text-gray-500">{{ $t('label.bgg_interval_hint') }}</small>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-4">
                        <button type="submit" class="db-btn text-white bg-primary flex items-center gap-2" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>{{ $t('label.save_module_config') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>

            <!-- TAB 2: FUENTES Y DOMINIOS WEB PREFERENTES -->
            <form v-else-if="activeModuleTab === 'web_sources'" @submit.prevent="saveImportSettings" class="w-full d-block">
                <div class="form-row">
                    <div class="form-col-12 border-b pb-3 mb-3">
                        <h4 class="font-client font-medium text-base">{{ $t('label.web_sources_title') }}</h4>
                        <p class="text-xs text-gray-500 mt-1">{{ $t('label.web_sources_desc') }}</p>
                    </div>

                    <div class="form-col-12">
                        <label class="db-field-title">{{ $t('label.ai_lookup_sources') }}</label>
                        <textarea
                            class="db-field-control font-mono text-xs"
                            rows="6"
                            v-model="lookupSourcesText"
                            :placeholder="$t('label.web_sources_textarea_hint')"
                        ></textarea>
                        <small class="text-xs text-gray-500">{{ $t('label.web_sources_hint') }}</small>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-4">
                        <button type="submit" class="db-btn text-white bg-primary flex items-center gap-2" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>{{ $t('label.save_web_sources_config') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import LoadingComponent from '../../components/LoadingComponent';
import alertService from '../../../../services/alertService';
import askEnum from '../../../../enums/modules/askEnum';
import activityEnum from '../../../../enums/modules/activityEnum';

export default {
    name: 'AiImportSettingComponent',
    components: { LoadingComponent },
    data() {
        return {
            loading: {
                isActive: false,
            },
            onUpdateActive: false,
            activeModuleTab: 'board_games',
            enums: {
                ask: askEnum,
                activity: activityEnum,
            },
            importForm: {
                is_enabled: true,
                max_items_per_document: 200,
                max_items_per_chunk: 20,
                default_provider: 'openai',
                default_model: '',
                default_user_prompt: '',
                default_custom_fields: {},
                default_wholesale: askEnum.NO,
                default_wholesale_min: null,
                default_tax_ids: [],
                default_only_stock: activityEnum.DISABLE,
                default_show_soldout: activityEnum.DISABLE,
                default_refundable: askEnum.YES,
                default_max_purchase_qty: 1,
                default_low_stock_alert_at: 1,
                default_unit_id: null,
                default_barcode_id: 1,
                default_warranty: '',
                extra: {
                    product_type_plugin: 'board_games',
                    web_search_order: 'after_plugin',
                    web_search_scope: 'open',
                    language: 'es',
                    system_prompt_mode: 'strict_json',
                    lookup_sources: [],
                    web_enrichment_enabled: false,
                    web_enrichment_mode: 'missing_dimensions_only',
                    web_enrichment_overwrite: false,
                    web_enrichment_image_enabled: false,
                    web_fallback_enabled: false,
                    web_fallback_mode: 'missing_dimensions_only',
                    web_fallback_overwrite: false,
                    bgg_api_enabled: true,
                    bgg_api_base_url: 'https://api.boardgamegeek.com/xmlapi2',
                    bgg_api_search_endpoint: '/search',
                    bgg_api_detail_endpoint: '/thing',
                    bgg_api_token: '',
                    bgg_api_safety_interval: 5,
                    image_pipeline: {
                        include_cover_image: true,
                        max_dimension: 1600,
                        min_dimension: 300,
                        optimization_percentage: 18,
                        treatment_type: 1,
                        padding_color: '#FFFFFF',
                        crop_x_align: 'center',
                        crop_y_align: 'center',
                    },
                },
            },
            providerRows: [],
            providerOptions: [],
            providerDefault: null,
            providerCatalogLoaded: false,
            languageCatalogLoaded: false,
            providerSelectKey: 0,
            modelSelectKey: 0,
            barcodeSelectKey: 0,
            languageSelectKey: 0,
            chunkSizeSelectKey: 0,
            documentSizeSelectKey: 0,
            chunkSizeOptions: [
                { id: 5, name: '5' },
                { id: 10, name: '10' },
                { id: 15, name: '15' },
                { id: 20, name: '20' },
            ],
            documentSizeOptions: [
                { id: 20, name: '20' },
                { id: 50, name: '50' },
                { id: 100, name: '100' },
                { id: 150, name: '150' },
                { id: 200, name: '200' },
            ],
            askOptions: [
                { id: askEnum.YES, name: this.$t('label.yes') },
                { id: askEnum.NO, name: this.$t('label.no') },
            ],
            activityOptions: [
                { id: activityEnum.ENABLE, name: this.$t('label.enable') },
                { id: activityEnum.DISABLE, name: this.$t('label.disable') },
            ],
            systemPromptModeOptions: [
                { id: 'strict_json', name: this.$t('label.system_prompt_mode_strict_json') },
                { id: 'balanced', name: this.$t('label.system_prompt_mode_balanced') },
                { id: 'creative', name: this.$t('label.system_prompt_mode_creative') },
            ],
            treatmentTypeOptions: [
                { id: 1, name: this.$t('label.image_treatment_type_1') },
                { id: 2, name: this.$t('label.image_treatment_type_2') },
                { id: 3, name: this.$t('label.image_treatment_type_3') }
            ],
            cropXAlignOptions: [
                { id: 'left', name: this.$t('label.image_align_left') },
                { id: 'center', name: this.$t('label.image_align_center') },
                { id: 'right', name: this.$t('label.image_align_right') }
            ],
            cropYAlignOptions: [
                { id: 'top', name: this.$t('label.image_align_top') },
                { id: 'center', name: this.$t('label.image_align_center') },
                { id: 'bottom', name: this.$t('label.image_align_bottom') }
            ],
            customFieldsJson: '{}',
            lookupSourcesText: '',
            barcodeTypeOptions: [],
            isRefreshingView: false,
            isSaving: false,
            errors: {},
        };
    },
    mounted() {
        this.fetchBootstrap();
    },
    computed: {
        availablePluginsOptions() {
            return [
                { id: 'board_games', name: `${this.$t('label.board_games_title')} (${this.$t('label.bgg_api_config')})` },
                { id: 'none', name: this.$t('label.general_plugin_option') },
            ];
        },
        webSearchOrderOptions() {
            return [
                { id: 'after_plugin', name: this.$t('label.web_search_order_after') },
                { id: 'before_plugin', name: this.$t('label.web_search_order_before') },
            ];
        },
        webSearchScopeOptions() {
            return [
                { id: 'open', name: this.$t('label.web_search_scope_open') },
                { id: 'restricted', name: this.$t('label.web_search_scope_restricted') },
            ];
        },
        taxes() {
            return this.$store.getters['tax/lists'] || [];
        },
        units() {
            return this.$store.getters['unit/lists'] || [];
        },
        taxOptions() {
            return this.taxes.map((item) => ({
                id: Number(item.id),
                name: item.name || item.code || `#${item.id}`,
            }));
        },
        unitOptions() {
            return this.units.map((item) => ({
                id: Number(item.id),
                name: item.name || item.code || `#${item.id}`,
            }));
        },
        languages() {
            return this.$store.getters['language/lists'] || [];
        },
        languageOptions() {
            return this.languages
                .filter((item) => Number(item.status) === 5)
                .map((item) => ({
                    id: item.code,
                    name: `${item.name} (${item.code})`,
                }));
        },
        safeLanguageOptions() {
            const options = [...this.languageOptions];
            const selectedLanguage = this.normalizeScalarSelectValue(this.importForm?.extra?.language);

            if (selectedLanguage && !options.some((item) => item.id === selectedLanguage)) {
                const fallbackLabel = this.languages.find((item) => item.code === selectedLanguage)?.name || selectedLanguage;
                options.push({
                    id: selectedLanguage,
                    name: `${fallbackLabel} (${selectedLanguage})`,
                });
            }

            return options.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
        safeProviderOptions() {
            return this.providerOptions.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
        activeModelOptions() {
            const provider = this.importForm.default_provider;
            const selectedModel = this.normalizeScalarSelectValue(this.importForm.default_model);

            return this.providerRows
                .filter((item) => item.provider === provider && (item.is_enabled || item.model === selectedModel))
                .filter((item) => !!item.model)
                .map((item) => ({
                    id: item.model,
                    name: item.model,
                }));
        },
        safeActiveModelOptions() {
            return this.activeModelOptions.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
        safeBarcodeTypeOptions() {
            const options = [...this.barcodeTypeOptions];
            const selectedBarcodeId = this.normalizeNumericSelectValue(this.importForm.default_barcode_id);

            if (selectedBarcodeId !== null && !options.some((item) => Number(item.id) === selectedBarcodeId)) {
                options.push({
                    id: selectedBarcodeId,
                    name: `#${selectedBarcodeId}`,
                });
            }

            return options.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
    },
    watch: {
        'importForm.default_provider'() {
            this.modelSelectKey += 1;
            this.ensureModelSelection();
        },
    },
    methods: {
        setActiveModuleTab(tabName) {
            this.activeModuleTab = tabName;
        },
        togglePlugin(pluginKey, isChecked) {
            if (isChecked) {
                // Regla de exclusividad: Desactiva cualquier otro plugin al activar uno nuevo
                this.importForm.extra.product_type_plugin = pluginKey;
                if (pluginKey === 'board_games') {
                    this.importForm.extra.bgg_api_enabled = true;
                    this.importForm.extra.board_games_enabled = true;
                    this.activeModuleTab = 'board_games';
                }
            } else {
                if (this.importForm.extra.product_type_plugin === pluginKey) {
                    this.importForm.extra.product_type_plugin = 'none';
                }
                if (pluginKey === 'board_games') {
                    this.importForm.extra.bgg_api_enabled = false;
                    this.importForm.extra.board_games_enabled = false;
                    if (this.activeModuleTab === 'board_games') {
                        this.activeModuleTab = 'web_sources';
                    }
                }
            }
            this.saveImportSettings();
        },
        normalizeScalarSelectValue(value) {
            if (value && typeof value === 'object') {
                return value.id ?? null;
            }

            return value ?? null;
        },
        normalizeNumericSelectValue(value) {
            const normalized = this.normalizeScalarSelectValue(value);

            if (normalized === null || normalized === undefined || normalized === '') {
                return null;
            }

            const numeric = Number(normalized);
            return Number.isFinite(numeric) ? numeric : null;
        },
        normalizeImportSettingsResponse(data = {}) {
            const taxIds = this.parseTaxIds(data.default_tax_ids);
            const defaultCustomFields = this.normalizeCustomFieldsValue(data?.default_custom_fields);
            const bggEnabled = data?.extra?.bgg_api_enabled !== undefined ? Boolean(data.extra.bgg_api_enabled) : true;
            const productTypePlugin = data?.extra?.product_type_plugin || (bggEnabled ? 'board_games' : 'none');

            return {
                ...data,
                is_enabled: data?.is_enabled !== undefined ? !!data.is_enabled : true,
                max_items_per_chunk: data?.max_items_per_chunk ? Number(data.max_items_per_chunk) : 10,
                max_items_per_document: data?.max_items_per_document ? Number(data.max_items_per_document) : 100,
                default_provider: this.normalizeScalarSelectValue(data?.default_provider),
                default_model: this.normalizeScalarSelectValue(data?.default_model),
                default_custom_fields: defaultCustomFields,
                default_unit_id: data?.default_unit_id ? Number(data.default_unit_id) : null,
                default_barcode_id: data?.default_barcode_id ? Number(data.default_barcode_id) : 1,
                default_tax_ids: taxIds,
                extra: {
                    product_type_plugin: productTypePlugin,
                    web_search_order: data?.extra?.web_search_order || 'after_plugin',
                    web_search_scope: data?.extra?.web_search_scope || 'open',
                    language: data?.extra?.language || 'es',
                    system_prompt_mode: data?.extra?.system_prompt_mode || 'strict_json',
                    lookup_sources: this.normalizeLookupSourcesValue(data?.extra?.lookup_sources),
                    web_enrichment_enabled: Boolean(data?.extra?.web_enrichment_enabled ?? data?.extra?.web_fallback_enabled),
                    web_enrichment_mode: data?.extra?.web_enrichment_mode || data?.extra?.web_fallback_mode || 'missing_dimensions_only',
                    web_enrichment_overwrite: Boolean(data?.extra?.web_enrichment_overwrite ?? data?.extra?.web_fallback_overwrite ?? false),
                    web_enrichment_image_enabled: Boolean(data?.extra?.web_enrichment_image_enabled ?? false),
                    web_fallback_enabled: Boolean(data?.extra?.web_fallback_enabled),
                    web_fallback_mode: data?.extra?.web_fallback_mode || 'missing_dimensions_only',
                    web_fallback_overwrite: Boolean(data?.extra?.web_fallback_overwrite ?? false),
                    bgg_api_enabled: bggEnabled,
                    board_games_enabled: bggEnabled,
                    bgg_api_base_url: data?.extra?.bgg_api_base_url || 'https://api.boardgamegeek.com/xmlapi2',
                    bgg_api_search_endpoint: data?.extra?.bgg_api_search_endpoint || '/search',
                    bgg_api_detail_endpoint: data?.extra?.bgg_api_detail_endpoint || '/thing',
                    bgg_api_token: data?.extra?.bgg_api_token || '',
                    bgg_api_safety_interval: (data?.extra?.bgg_api_safety_interval !== undefined && data?.extra?.bgg_api_safety_interval !== null && data?.extra?.bgg_api_safety_interval !== '') ? Number(data.extra.bgg_api_safety_interval) : 5,
                    image_pipeline: {
                        include_cover_image: data?.extra?.image_pipeline?.include_cover_image !== undefined ? Boolean(data.extra.image_pipeline.include_cover_image) : true,
                        max_dimension: data?.extra?.image_pipeline?.max_dimension ?? 1600,
                        min_dimension: data?.extra?.image_pipeline?.min_dimension ?? 300,
                        optimization_percentage: data?.extra?.image_pipeline?.optimization_percentage ?? 18,
                        treatment_type: data?.extra?.image_pipeline?.treatment_type !== undefined ? Number(data.extra.image_pipeline.treatment_type) : 1,
                        padding_color: data?.extra?.image_pipeline?.padding_color || '#FFFFFF',
                        crop_x_align: data?.extra?.image_pipeline?.crop_x_align || 'center',
                        crop_y_align: data?.extra?.image_pipeline?.crop_y_align || 'center',
                    },
                },
            };
        },
        normalizeCustomFieldsValue(value) {
            if (Array.isArray(value)) {
                return value;
            }

            if (value && typeof value === 'object') {
                return value;
            }

            return {};
        },
        normalizeLookupSourcesValue(value) {
            if (Array.isArray(value)) {
                return [...new Set(value
                    .map((item) => String(item || '').trim())
                    .filter((item) => item !== '')
                )];
            }

            if (typeof value === 'string') {
                return [...new Set(value
                    .split(/\r?\n|,/)
                    .map((item) => item.trim())
                    .filter((item) => item !== '')
                )];
            }

            return [];
        },
        parseTaxIds(raw) {
            if (Array.isArray(raw)) {
                return raw.map((id) => Number(id)).filter((id) => id > 0);
            }

            if (typeof raw === 'string') {
                return raw
                    .split(',')
                    .map((id) => Number(id.trim()))
                    .filter((id) => id > 0);
            }

            return [];
        },
        fetchBootstrap() {
            // Bootstrap catalog data
            this.loading.isActive = true;
            return Promise.all([
                this.$store.dispatch('tax/lists', { vuex: true }),
                this.$store.dispatch('unit/lists', { vuex: true }),
                this.$store.dispatch('language/lists', { vuex: true }),
                this.fetchImportSettings(),
                this.fetchProviders(),
            ])
                .catch((error) => {
                    alertService.error(error?.response?.data?.message || this.$t('message.something_went_wrong'));
                })
                .finally(() => {
                    this.loading.isActive = false;
                });
        },
        fetchProviders() {
            return this.$store.dispatch('aiProviderSetting/providerSettings')
                .then((res) => {
                    this.providerRows = res?.data?.data || [];
                    this.providerOptions = (res?.data?.providers || []).map((item) => ({
                        id: item.provider,
                        name: item.name || item.provider,
                    }));
                    this.providerDefault = res?.data?.default_provider || null;
                    this.providerCatalogLoaded = true;
                    this.ensureProviderSelection();
                    this.ensureModelSelection();
                });
        },
        fetchImportSettings() {
            return this.$store.dispatch('aiImportSetting/importSetting')
                .then((res) => {
                    const payload = this.normalizeImportSettingsResponse(res?.data?.data || {});
                    this.importForm = payload;
                    if (!payload.extra?.bgg_api_enabled && this.activeModuleTab === 'board_games') {
                        this.activeModuleTab = 'web_sources';
                    }
                    this.customFieldsJson = JSON.stringify(payload.default_custom_fields || {}, null, 2);
                    this.lookupSourcesText = (payload?.extra?.lookup_sources || []).join('\n');
                    this.barcodeTypeOptions = res?.data?.data?.barcode_type_options || [];
                    this.ensureProviderSelection();
                    this.ensureModelSelection();
                });
        },
        ensureProviderSelection() {
            const currentProvider = this.normalizeScalarSelectValue(this.importForm.default_provider);

            if (!currentProvider || !this.safeProviderOptions.some((item) => item.id === currentProvider)) {
                const fallbackProvider = this.providerDefault || this.safeProviderOptions[0]?.id || 'openai';
                this.importForm.default_provider = fallbackProvider;
            } else {
                this.importForm.default_provider = currentProvider;
            }

            this.providerSelectKey += 1;
        },
        ensureModelSelection() {
            const currentModel = this.normalizeScalarSelectValue(this.importForm.default_model);

            if (this.safeActiveModelOptions.length === 0) {
                this.importForm.default_model = currentModel || '';
                return;
            }

            if (!currentModel || !this.safeActiveModelOptions.some((item) => item.id === currentModel)) {
                this.importForm.default_model = this.safeActiveModelOptions[0].id;
            } else {
                this.importForm.default_model = currentModel;
            }

            this.modelSelectKey += 1;
        },
        refreshFromServer() {
            this.isRefreshingView = true;
            this.fetchBootstrap()
                .then(() => {
                    alertService.success(this.$t('message.ai_import_refreshed'));
                })
                .finally(() => {
                    this.isRefreshingView = false;
                });
        },
        saveImportSettings() {
            let customFieldsObj = {};
            try {
                customFieldsObj = JSON.parse(this.customFieldsJson || '{}');
            } catch (e) {
                alertService.error(this.$t('message.invalid_custom_fields_json'));
                return;
            }

            const lookupSources = (this.lookupSourcesText || '')
                .split('\n')
                .map((item) => item.trim())
                .filter((item) => item !== '');

            const isBggEnabled = Boolean(this.importForm.extra?.bgg_api_enabled);

            const payload = {
                ...this.importForm,
                default_provider: this.normalizeScalarSelectValue(this.importForm.default_provider),
                default_model: this.normalizeScalarSelectValue(this.importForm.default_model),
                default_unit_id: this.normalizeNumericSelectValue(this.importForm.default_unit_id),
                default_barcode_id: this.normalizeNumericSelectValue(this.importForm.default_barcode_id),
                default_custom_fields: customFieldsObj,
                extra: {
                    ...this.importForm.extra,
                    product_type_plugin: isBggEnabled ? (this.importForm.extra.product_type_plugin || 'board_games') : 'none',
                    lookup_sources: lookupSources,
                    web_enrichment_enabled: Boolean(this.importForm.extra.web_enrichment_enabled),
                    web_enrichment_mode: this.importForm.extra.web_enrichment_mode || 'missing_dimensions_only',
                    web_enrichment_overwrite: Boolean(this.importForm.extra.web_enrichment_overwrite),
                    web_enrichment_image_enabled: Boolean(this.importForm.extra.web_enrichment_image_enabled),
                    web_fallback_enabled: Boolean(this.importForm.extra.web_enrichment_enabled),
                    web_fallback_mode: this.importForm.extra.web_enrichment_mode || 'missing_dimensions_only',
                    web_fallback_overwrite: Boolean(this.importForm.extra.web_enrichment_overwrite),
                    bgg_api_enabled: isBggEnabled,
                    board_games_enabled: isBggEnabled,
                    image_pipeline: {
                        ...this.importForm.extra.image_pipeline,
                        include_cover_image: Boolean(this.importForm.extra.image_pipeline.include_cover_image),
                        max_dimension: Number(this.importForm.extra.image_pipeline.max_dimension),
                        min_dimension: Number(this.importForm.extra.image_pipeline.min_dimension),
                        optimization_percentage: Number(this.importForm.extra.image_pipeline.optimization_percentage),
                        treatment_type: Number(this.importForm.extra.image_pipeline.treatment_type),
                    },
                },
            };

            this.isSaving = true;
            this.errors = {};

            this.$store.dispatch('aiImportSetting/saveImportSetting', payload)
                .then((res) => {
                    const normalized = this.normalizeImportSettingsResponse(res?.data?.data || {});
                    this.importForm = normalized;
                    this.customFieldsJson = JSON.stringify(normalized.default_custom_fields || {}, null, 2);
                    this.lookupSourcesText = (normalized?.extra?.lookup_sources || []).join('\n');
                    alertService.success(this.$t('message.ai_import_settings_saved'));
                })
                .catch((err) => {
                    this.errors = err?.response?.data?.errors || {};
                    alertService.error(err?.response?.data?.message || this.$t('message.something_went_wrong'));
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },
    },
};
</script>
